import React, { useState, useEffect } from "react";
import { Modal as OpusModal, Icon as OpusIcon } from "../common/opus";
import { LAYERS, DEFAULT_LAYER, normalizeLayer, layerMeta } from "../../services/scenes/captionPayload";

/**
 * AddCaptionDialog — two-step Add Caption flow (OPUS-1391).
 *
 *   pick a layer  →  create manually / upload a zip
 *
 * The layer is asked FIRST because it decides where the caption ends up, so both
 * creation paths — including a round trip out to the caption editor and back —
 * already know the destination. The chosen layer is handed to whichever callback
 * fires, so the caller never has to track it.
 *
 * WHY THIS IS ITS OWN COMPONENT
 *
 * This state machine started life as four handlers and two pieces of state smeared
 * across the 1000-line scene editor page, where nothing could test it. A mechanical
 * find-and-replace during a refactor turned the close handler into an infinite
 * recursion — `closeAddCaptionDialog` calling itself — which crashed every route
 * out of the dialog: create manually, upload zip and cancel. The whole suite still
 * passed, because the test exercised a hand-written mirror of the logic rather than
 * the real thing.
 *
 * Owning the state here means the component under test IS the code that ships.
 */
const AddCaptionDialog = ({ open, onClose, onCreateManually, onUploadZip }) => {
  const [step, setStep] = useState("layer");
  const [layer, setLayer] = useState(DEFAULT_LAYER);

  // Reopening always starts at step one on the default layer. Without this,
  // reopening would land on the method screen carrying the previously chosen
  // layer and silently add the caption to the wrong one.
  useEffect(() => {
    if (open) {
      setStep("layer");
      setLayer(DEFAULT_LAYER);
    }
  }, [open]);

  const chooseLayer = (value) => {
    setLayer(normalizeLayer(value));
    setStep("method");
  };

  return (
    <OpusModal
      open={open}
      onClose={onClose}
      title="Add Caption"
      subtitle={
        step === "layer"
          ? "Which layer should this caption render on?"
          : `Adding to ${layerMeta(layer).label} — create manually or upload a caption zip?`
      }
      icon="plus"
      width={430}
      footer={null}
    >
      {step === "layer" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {LAYERS.map((l) => (
            <button key={l.value} type="button" onClick={() => chooseLayer(l.value)}
              style={{ display: "flex", alignItems: "center", gap: 11, width: "100%", padding: "12px 14px", borderRadius: "var(--r-md)", border: "1px solid var(--border)", background: "var(--surface-2)", cursor: "pointer", fontFamily: "var(--font-sans)", textAlign: "left" }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: l.color, flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{l.label}</span>
                <span style={{ display: "block", fontSize: 11.5, color: "var(--text-dim)", marginTop: 2 }}>{l.desc}</span>
              </span>
              <OpusIcon name="chevronDown" size={15} style={{ color: "var(--text-dim)", transform: "rotate(-90deg)", flexShrink: 0 }} />
            </button>
          ))}
          <button type="button" onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 13.5, fontWeight: 600, fontFamily: "var(--font-sans)", padding: 8, marginTop: 2 }}>Cancel</button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[
            { icon: "plus", label: "Create manually", onClick: () => onCreateManually(layer) },
            { icon: "upload", label: "Upload caption zip", onClick: () => onUploadZip(layer) },
          ].map((c) => (
            <button key={c.label} type="button" onClick={c.onClick}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 9, width: "100%", padding: 12, borderRadius: "var(--r-md)", border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--green)", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-sans)" }}>
              <OpusIcon name={c.icon} size={17} />{c.label}
            </button>
          ))}
          <button type="button" onClick={() => setStep("layer")}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 13.5, fontWeight: 600, fontFamily: "var(--font-sans)", padding: 8, marginTop: 2 }}>Back</button>
        </div>
      )}
    </OpusModal>
  );
};

export default AddCaptionDialog;
